import React from 'react';
import { motion } from 'framer-motion';
import { Mail, School, ChevronRight, Home, ShieldCheck, Search, Send } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import type { RegistrySchool } from '../../../data/supabase/schools';
type Role = 'learner' | 'parent' | 'teacher';

/**
 * Hoe deze leerkracht lesgeeft. Bewust een EXPLICIETE keuze.
 *
 * Hiervoor was "ik geef bijles" hetzelfde als "veld leeg laten", uitgelegd in
 * een grijze regel onder het invoerveld. Een lege waarde is geen antwoord: je
 * kunt er niet aan zien of iemand zelfstandig werkt of het veld heeft
 * overgeslagen. En juist dat verschil bepaalt de hele route erna — via een
 * school met verwerkersovereenkomst, of via de ouder die je koppelt.
 */
export type SchoolMode = 'unset' | 'school' | 'independent';
interface RegisterDetailsStepProps {
  role: Role;
  grade: string;
  onGradeChange: (grade: string) => void;
  email: string;
  onEmailChange: (email: string) => void;
  schoolName: string;
  onSchoolNameChange: (name: string) => void;
  schoolSearch: string;
  onSchoolSearchChange: (search: string) => void;
  schoolMode: SchoolMode;
  onSchoolModeChange: (mode: SchoolMode) => void;
  /** Staat de school niet in het register? Dan is dit een aanvraag, geen keuze. */
  schoolIsRequest: boolean;
  onSchoolIsRequestChange: (isRequest: boolean) => void;
  /**
   * Zet het id van de gekozen school (0041), of leegt het bij een aanvraag.
   *
   * Het id zelf komt hier niet terug als prop: dit scherm toont de NAAM en heeft
   * het id alleen te zetten. Zou het als leesbare prop bestaan, dan zou er een
   * tweede plek zijn die kan bepalen of dit een keuze of een aanvraag is —
   * `schoolIsRequest` doet dat al voor de tekst, en de server gaat af op het id.
   */
  onSchoolIdChange: (id: string) => void;
  registrySchools: RegistrySchool[];
  registryLoading: boolean;
  onNext: () => void;
  slideVariants: Record<string, unknown>;
}
export function RegisterDetailsStep({
  role,
  grade,
  onGradeChange,
  email,
  onEmailChange,
  schoolName,
  onSchoolNameChange,
  schoolSearch,
  onSchoolSearchChange,
  schoolMode,
  onSchoolModeChange,
  schoolIsRequest,
  onSchoolIsRequestChange,
  onSchoolIdChange,
  registrySchools,
  registryLoading,
  onNext,
  slideVariants
}: RegisterDetailsStepProps) {
  const itemVariants = {
    enter: {
      opacity: 0,
      y: 20
    },
    center: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: -20
    }
  };
  const query = schoolSearch.trim().toLowerCase();
  const matches = query ? registrySchools.filter(s => s.name.toLowerCase().includes(query) || (s.city ?? '').toLowerCase().includes(query)) : registrySchools;
  const resetSchool = () => {
    onSchoolNameChange('');
    onSchoolSearchChange('');
    onSchoolIsRequestChange(false);
    // Ook het id, anders blijft een eerdere keuze achter terwijl het scherm
    // leeg staat — en dan gaat er een school mee die de leerkracht niet ziet.
    onSchoolIdChange('');
  };

  // Verder mogen is per rol iets anders. Voor een leerkracht is een half
  // ingevulde schoolkeuze erger dan geen: dan staat er straks een naam bij zijn
  // account waarvan niemand weet of hij gekozen of getypt is.
  const canContinue = role !== 'teacher' ? Boolean(email.trim()) : Boolean(email.trim()) && (schoolMode === 'independent' || schoolMode === 'school' && Boolean(schoolName.trim()));
  return <motion.div key="step-details" custom={1} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
    duration: 0.3,
    staggerChildren: 0.1
  }} className="space-y-5" data-id="element-329" data-mp="34t1zb5nt40c3">
      
      <motion.h2 variants={itemVariants} className="text-xl font-black text-slate-900 text-center" data-id="element-330" data-mp="2v3ig1yw6m4gp">
        
        {role === 'learner' ? 'In welke groep zit je?' : role === 'teacher' ? 'Waar geef je les?' : 'Nog een paar details'}
      </motion.h2>

      {/* Learner: Grade selection */}
      {role === 'learner' && <motion.div variants={itemVariants} className="grid grid-cols-3 gap-3" data-id="element-331" data-mp="388leqm05n92e">
          {['6', '7', '8'].map(g => {
        const isActive = grade === g;
        const emoji = g === '6' ? '🌟' : g === '7' ? '🚀' : '🏆';
        return <motion.button key={g} type="button" onClick={() => {
          onGradeChange(g);
          setTimeout(onNext, 350);
        }} className={`py-6 rounded-2xl border-2 text-center transition-colors relative overflow-hidden ${isActive ? 'border-orange-400 bg-orange-50 text-orange-700 shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-orange-200 hover:bg-orange-50/50'}`} whileTap={{
          scale: 0.95
        }} animate={isActive ? {
          scale: [1, 1.1, 1.05],
          y: -4
        } : {
          scale: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 15
        }} data-id="element-332" data-mp="2ydbea2pen9qq">
              
                <div className="absolute -right-2 -bottom-2 text-4xl opacity-10 grayscale filter" data-id="element-333" data-mp="ilx8vzb33wfq">
                  {emoji}
                </div>
                <div className="relative z-10" data-id="element-334" data-mp="1ybwqylq3runa">
                  <div className="text-2xl mb-1" data-id="element-335" data-mp="1yb57g99s5ndd">{emoji}</div>
                  <div className="text-xs font-bold opacity-60 mb-1" data-id="element-336" data-mp="2c3l6z03h339j">Groep</div>
                  <div className="text-3xl font-black" data-id="element-337" data-mp="281gg294eskql">{g}</div>
                </div>
              </motion.button>;
      })}
        </motion.div>}

      {/* Parent: Email */}
      {role === 'parent' && <motion.div variants={itemVariants} data-id="element-338" data-mp="1fcsm7p5lgn1f">
          <label className="block text-sm font-bold text-slate-600 mb-1.5" htmlFor="register-parent-email" data-id="element-339" data-mp="1fdu70lcewnwc">
            E-mailadres
          </label>
          <div className="relative" data-id="element-340" data-mp="1ta7j1ey6fx3f">
            <Input id="register-parent-email" type="email" required autoFocus value={email} onChange={e => onEmailChange(e.target.value)} icon={<Mail className="w-5 h-5" data-id="element-342" data-mp="34pw2vxg4wg00" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium py-4" placeholder="naam@voorbeeld.nl" data-id="element-341" data-mp="1od7ifjk3jmjp" />
          
          </div>
        </motion.div>}

      {/* Teacher: Email + school route */}
      {role === 'teacher' && <motion.div variants={itemVariants} className="space-y-5" data-id="element-343" data-mp="22gfc43m5bfb5">
          
          {/*
           Dit stond nergens in de registratie — de uitleg begon pas ná het
           inloggen, in een banner. Een leerkracht die dat niet weet, maakt een
           account, ziet een leeg portaal en concludeert dat de app stuk is.
           Verwachting managen hoort vóór de handeling, niet erna.
           */}
          <div className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 rounded-2xl" data-id="element-344" data-mp="1inz6j85y5og9">
            <ShieldCheck className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" data-id="element-345" data-mp="1sg4y4gsae3ux" />
            <div className="text-xs leading-relaxed text-slate-600" data-id="element-346" data-mp="1si7zty2sxlfh">
              <span className="font-bold text-slate-900" data-id="element-347" data-mp="uzzjojbrai6y">
                Je account wordt eerst gecontroleerd.
              </span>{' '}
              Omdat je bij gegevens van kinderen kunt, geven we niet meteen
              toegang. Je kunt de app wel alvast bekijken; je klassen blijven
              leeg tot de controle rond is.
            </div>
          </div>

          <div data-id="element-348" data-mp="ihlyrssus9oe">
            <label className="block text-sm font-bold text-slate-600 mb-1.5" htmlFor="register-teacher-email" data-id="element-349" data-mp="ih3vwdboqx7i">
              E-mailadres
            </label>
            <div className="relative" data-id="element-350" data-mp="9hgs5kvhhfba">
              <Input id="register-teacher-email" type="email" required autoFocus value={email} onChange={e => onEmailChange(e.target.value)} icon={<Mail className="w-5 h-5" data-id="element-352" data-mp="4pflqfkzyit6" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium py-4" placeholder="naam@school.nl" data-id="element-351" data-mp="9iig2pxllwaf" />
            
            </div>
          </div>

          {/* Stap 1 — de route. Zie SchoolMode hierboven voor het waarom. */}
          {schoolMode === 'unset' && <div className="space-y-2.5" data-id="element-353" data-mp="3hwqturvgoiij">
              <button type="button" onClick={() => onSchoolModeChange('school')} className="w-full flex items-start gap-3 p-4 bg-white border-2 border-slate-200 rounded-2xl text-left transition-colors hover:border-orange-300 hover:bg-orange-50/50" data-id="element-354" data-mp="3hy251cr8ksjo">
            
                <School className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" data-id="element-355" data-mp="10onpitn48orq" />
                <div data-id="element-356" data-mp="14oy7l4o6n5my">
                  <p className="font-bold text-slate-900" data-id="element-357" data-mp="14og3xmz6xp50">Ik werk op een school</p>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-358" data-mp="qxkti691mzzb">
                    Je collega's kunnen je toevoegen aan een klas.
                  </p>
                </div>
              </button>

              <button type="button" onClick={() => {
          onSchoolModeChange('independent');
          resetSchool();
        }} className="w-full flex items-start gap-3 p-4 bg-white border-2 border-slate-200 rounded-2xl text-left transition-colors hover:border-orange-300 hover:bg-orange-50/50" data-id="element-359" data-mp="h0exqkphiksr">
            
                <Home className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" data-id="element-360" data-mp="cx9ziucft59x" />
                <div data-id="element-361" data-mp="3phe0tv92gp7j">
                  <p className="font-bold text-slate-900" data-id="element-362" data-mp="3pip3frqrzqx2">
                    Ik geef bijles of thuisonderwijs
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-363" data-mp="3kkxen6lfgkx3">
                    Je krijgt toegang per kind, via de ouder die je koppelt.
                  </p>
                </div>
              </button>
            </div>}

          {/* Zelfstandig — geen school, en dat is een antwoord. */}
          {schoolMode === 'independent' && <div className="space-y-2" data-id="element-364" data-mp="7cki7o4xs282">
              <div className="flex items-start gap-3 p-4 bg-orange-50 border-2 border-orange-200 rounded-2xl" data-id="element-365" data-mp="7bhrphbj01so">
                <Home className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" data-id="element-366" data-mp="m0ns38m3ny25" />
                <div className="flex-1" data-id="element-367" data-mp="h35h7coiv8ri">
                  <p className="font-bold text-slate-900" data-id="element-368" data-mp="h4gh31eujwdc">
                    Bijles of thuisonderwijs
                  </p>
                  {/*
                   Dit is geen beperking maar de grondslag. Bij een school is de
                   school verwerkingsverantwoordelijke; die relatie draagt de
                   inzage. Een zelfstandige heeft die niet, dus loopt zijn
                   toegang via de ouder — die hem ook weer kan losmaken.
                   */}
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed" data-id="element-369" data-mp="12u70721nira4">
                    Een ouder koppelt jou aan zijn kind. Je ziet dan alleen dat
                    kind, en de ouder kan de koppeling altijd weer intrekken.
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => onSchoolModeChange('unset')} className="text-sm font-bold text-slate-500 hover:text-orange-600 transition-colors" data-id="element-370" data-mp="1qk1fdq9g0rxj">
            
                ← Toch een school kiezen
              </button>
            </div>}

          {/* School — kiezen uit het register, of aanvragen. */}
          {schoolMode === 'school' && <div className="space-y-3" data-id="element-371" data-mp="55nflkj5gync">
              {schoolName ? <div className="space-y-2" data-id="element-372" data-mp="589nxmdv7i6m">
                  <div className={`flex items-start gap-3 p-4 rounded-2xl border-2 ${schoolIsRequest ? 'bg-slate-50 border-slate-200' : 'bg-orange-50 border-orange-200'}`} data-id="element-373" data-mp="2gu0myuwkrp94">
                    {schoolIsRequest ? <Send className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" data-id="element-374" data-mp="2qr6fm7ku7obg" /> : <School className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" data-id="element-375" data-mp="2qplluk6ck9en" />}
                    <div className="flex-1 min-w-0" data-id="element-376" data-mp="2vjptl5nfslgs">
                      <p className="font-bold text-slate-900 truncate" data-id="element-377" data-mp="30i9fx75xzz9o">
                        {schoolName}
                      </p>
                      <p className="text-xs text-slate-600 mt-0.5 leading-relaxed" data-id="element-378" data-mp="34hi9oc0zfx58">
                        {schoolIsRequest ? 'Nog niet bij ons bekend. We nemen contact op met deze school voordat je toegang krijgt.' : 'Gekozen uit onze lijst met scholen.'}
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={resetSchool} className="text-sm font-bold text-slate-500 hover:text-orange-600 transition-colors" data-id="element-379" data-mp="1k3879ydjzz7v">
                
                    ← Andere school
                  </button>
                </div> : <>
                  <Input type="text" value={schoolSearch} onChange={e => onSchoolSearchChange(e.target.value)} icon={<Search className="w-5 h-5" data-id="element-381" data-mp="1m1j0ofsrqyie" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium py-4" placeholder="Zoek je school op naam of plaats..." autoComplete="off" autoFocus aria-label="Zoek je school" data-id="element-380" data-mp="1829v0a26vmso" />

                  {registryLoading ? <p className="text-sm text-slate-500 px-1" data-id="element-382" data-mp="1qzb3w5yv671w">Scholen laden…</p> : matches.length > 0 ? <div className="bg-white border-2 border-slate-200 rounded-2xl overflow-hidden max-h-56 overflow-y-auto" data-id="element-383" data-mp="24q65cx719lvg">
                      {matches.map(school => <button key={school.id} type="button" onClick={() => {
              // Naam ÉN id. De naam is alleen om te tonen; het id is wat
              // naar de server gaat en waar de beheerder op afgaat.
              onSchoolNameChange(school.name);
              onSchoolIdChange(school.id);
              onSchoolIsRequestChange(false);
              onSchoolSearchChange('');
            }} className="w-full text-left px-4 py-3.5 hover:bg-orange-50 transition-colors border-b border-slate-100 last:border-0 flex items-center gap-3" data-id="element-384" data-mp="1zuh50rn6w0y3">
                    
                          <School className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-385" data-mp="2ob6f3avhsbkl" />
                          <span className="flex-1 min-w-0" data-id="element-386" data-mp="29mjzr95v7jtd">
                            <span className="block font-medium text-slate-700 truncate" data-id="element-387" data-mp="2efd4hx1c2s5g">
                              {school.name}
                            </span>
                            {school.city && <span className="block text-xs text-slate-400" data-id="element-388" data-mp="25m9trwi2otei">
                                {school.city}
                              </span>}
                          </span>
                        </button>)}
                    </div> : null}

                  {/*
                   De aanvraagroute. Bewust GEEN "voeg handmatig toe": een
                   leerkracht die zelf een school aanmaakt, verifieert zichzelf.
                   Wat hij hier typt is een aanvraag die wij nakijken — en de
                   knop zegt dat, zodat niemand denkt dat hij klaar is.
                   */}
                  {!registryLoading && <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2" data-id="element-389" data-mp="2kb4ulu7rm4u4">
                      <p className="text-xs leading-relaxed text-slate-600" data-id="element-390" data-mp="2fcbio684mlw2">
                        {registrySchools.length === 0 ? 'Staat jouw school er nog niet bij? Vul de naam in, dan nemen wij contact op met de school.' : 'Niet gevonden? Vul de naam in, dan nemen wij contact op met de school.'}
                      </p>
                      <Button type="button" variant="secondary" size="sm" className="w-full" disabled={schoolSearch.trim().length < 2} onClick={() => {
              // Een aanvraag heeft géén id — dat is precies wat hem een
              // aanvraag maakt. Het id expliciet leegmaken en niet aannemen
              // dat het al leeg is: iemand kan eerst een school hebben
              // gekozen en daarna alsnog zijn eigen naam intikken.
              onSchoolNameChange(schoolSearch.trim());
              onSchoolIdChange('');
              onSchoolIsRequestChange(true);
              onSchoolSearchChange('');
            }} data-id="element-391" data-mp="21dbuezoj090f">
                    
                        <Send className="w-4 h-4 mr-1.5" data-id="element-392" data-mp="3kaounq8bpfqb" />
                        Deze school aanvragen
                      </Button>
                    </div>}

                  <button type="button" onClick={() => onSchoolModeChange('unset')} className="text-sm font-bold text-slate-500 hover:text-orange-600 transition-colors" data-id="element-393" data-mp="3pbcoskrumcor">
                
                    ← Ik werk niet op een school
                  </button>
                </>}
            </div>}
        </motion.div>}

      {/* Next button for non-learner (learner auto-advances on grade pick) */}
      {role !== 'learner' && <motion.div variants={itemVariants} data-id="element-394" data-mp="3akd9826091nr">
          <Button type="submit" variant="primary" size="lg" className="w-full" disabled={!canContinue} data-id="element-395" data-mp="3aj21jr9fv9rd">
          
            Verder <ChevronRight className="w-5 h-5 ml-1" data-id="element-396" data-mp="35l0gx1wd72ds" />
          </Button>
        </motion.div>}
    </motion.div>;
}